import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Loader2, RefreshCw } from 'lucide-react';
import { MatchCard } from '../components/MatchCard';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HomePage = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMatches = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/matches`);
      // Sort: live first, then upcoming by date, then finished
      const sorted = response.data.sort((a, b) => {
        const statusOrder = { live: 0, upcoming: 1, finished: 2 };
        if (statusOrder[a.status] !== statusOrder[b.status]) {
          return statusOrder[a.status] - statusOrder[b.status];
        }
        return new Date(a.match_date + 'T' + a.match_time) - new Date(b.match_date + 'T' + b.match_time);
      });
      setMatches(sorted);
    } catch (err) {
      console.error('Error fetching matches:', err);
      setError('Erro ao carregar jogos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const seedData = async () => {
    try {
      await axios.post(`${API}/seed`);
      fetchMatches();
    } catch (err) {
      console.error('Error seeding data:', err);
    }
  };

  useEffect(() => {
    fetchMatches();
  }, []);

  const upcomingMatches = matches.filter(m => m.status === 'upcoming' || m.status === 'live');
  const featuredMatch = upcomingMatches[0];
  const otherMatches = upcomingMatches.slice(1);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4" data-testid="loading-state">
        <Loader2 className="w-8 h-8 text-neon-green animate-spin" />
        <p className="text-zinc-400">Carregando jogos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4" data-testid="error-state">
        <p className="text-zinc-400">{error}</p>
        <Button onClick={fetchMatches} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="home-page">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">
            Jogos de <span className="text-neon-green">Hoje</span>
          </h1>
          <p className="text-zinc-400 mt-1">Veja onde assistir aos jogos de futebol ao vivo</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={fetchMatches} 
            variant="outline" 
            size="sm"
            className="gap-2 border-zinc-700 hover:border-neon-green/50"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </Button>
        </div>
      </div>

      {matches.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-6 border border-dashed border-zinc-800 bg-card-dark/50">
          <Calendar className="w-16 h-16 text-zinc-600" />
          <div className="text-center">
            <p className="text-zinc-400 text-lg">Nenhum jogo cadastrado</p>
            <p className="text-zinc-500 text-sm mt-1">Clique abaixo para adicionar jogos de exemplo</p>
          </div>
          <Button 
            onClick={seedData}
            className="bg-neon-green text-black font-bold uppercase tracking-wider hover:bg-white skew-x-[-5deg]"
            data-testid="seed-btn"
          >
            <span className="skew-x-[5deg]">Adicionar Jogos de Exemplo</span>
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Featured Match */}
          {featuredMatch && (
            <div>
              <h2 className="font-heading text-lg font-bold uppercase tracking-wider text-zinc-400 mb-3 flex items-center gap-2">
                <span className="w-1 h-5 bg-neon-green"></span>
                Próximo Jogo
              </h2>
              <MatchCard match={featuredMatch} featured />
            </div>
          )}

          {/* Other Upcoming Matches */}
          {otherMatches.length > 0 && (
            <div>
              <h2 className="font-heading text-lg font-bold uppercase tracking-wider text-zinc-400 mb-3 flex items-center gap-2">
                <span className="w-1 h-5 bg-neon-yellow"></span>
                Mais Jogos
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {otherMatches.map((match) => (
                  <MatchCard key={match.id} match={match} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HomePage;
